import React from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Text} from '../../atoms/Text/Text';
import {InfoCircleWithDialog} from '../../organisms/InfoCircle/InfoCircleWithDialog';
import bannerBackground from './assets/bannerBackground.svg';

interface CouponBannerProps {
  className?: string;
  ['data-testid']?: string;
  onClick?: () => void;
  barText: string | React.ReactNode;
  headline: string;
  subline: string;
  conditionText?: string;
  setGradientBackground?: boolean;
  hideCountdown: boolean;
}

export const CouponBanner = (
  props: CouponBannerProps & {ref?: React.Ref<HTMLButtonElement>},
) => {
  const {
    className,
    barText,
    headline,
    subline,
    conditionText,
    onClick,
    setGradientBackground,
    hideCountdown,
    ref,
  } = props;

  const handleOnclick = () => {
    onClick?.();
  };

  return (
    <LinkAndInfoIconWrapper data-testid={props['data-testid']}>
      <BannerButton
        className={className}
        onClick={handleOnclick}
        ref={ref}
        aria-label={undefined}
      >
        <Header $setGradientBackground={setGradientBackground}>
          <span>{barText}</span>
          <Icon
            data-testid="arrowLongRightIcon"
            asset={GenericIconName.ArrowSmallRight}
            size="XS"
            fill={COLORS.White}
          />
        </Header>
        <Content data-add-space={Boolean(conditionText)}>
          <CouponIcon>
            <Icon
              data-testid="couponIcon"
              asset={GenericIconName.Coupon}
              size="SS"
              fill={COLORS.White}
            />
          </CouponIcon>
          <TextWrapper>
            <Headline size="px14" weight="w500" color="PrimaryCoupon">
              {headline}
            </Headline>
            <Subline size="px12" weight="w400" color="PrimaryCoupon">
              {subline}
            </Subline>
          </TextWrapper>
        </Content>
      </BannerButton>
      {conditionText && (
        <StyledInfoCircleWithDialog
          conditions={conditionText}
          iconSize={'SS'}
          infoIconVariant="filled"
          hideCountdown={hideCountdown}
        />
      )}
    </LinkAndInfoIconWrapper>
  );
};

const LinkAndInfoIconWrapper = styled.div`
  position: relative;
`;

const StyledInfoCircleWithDialog = styled(InfoCircleWithDialog)`
  &&&& {
    top: 38px;
    right: 20px;
    background: ${COLORS.PrimaryCoupon};
    border-radius: 100%;
  }
`;

const Header = styled.div<{$setGradientBackground?: boolean}>`
  background-color: ${COLORS.PrimaryCoupon};
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 1.6;
  padding: 4px 21px;
  display: flex;
  justify-content: space-between;

  ${({$setGradientBackground}) =>
    $setGradientBackground &&
    css`
      background: linear-gradient(
        90deg,
        #1f4bff 46.15%,
        #e5177a 74.76%,
        #e52317 90.03%
      );
    `}
`;

const Content = styled.div`
  --inline-padding: 21px;
  display: flex;
  gap: 8px;
  width: 100%;
  padding: 12px 21px;
  background-image: url(${bannerBackground});
  background-repeat: no-repeat;
  background-size: cover;
  padding-right: var(--inline-padding);

  &[data-add-space='true'] {
    // prettier-ignore
    --inline-padding: calc(21px + 28px); // add 28px to give space to the info icon
  }
`;

const CouponIcon = styled.div`
  align-items: center;
  aspect-ratio: 1/1;
  background-color: ${COLORS.PrimaryCoupon};
  border-radius: 50%;
  display: flex;
  height: 34px;
  justify-content: center;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const Headline = styled(Text)`
  line-height: 1.42;
`;

const Subline = styled(Text)`
  line-height: 1.5;
`;

const BannerButton = styled(UnstyledButton)`
  width: 100%;
`;
